/**
 * Functional Select UI Component
 */
import React, { createContext, useContext, useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';

interface SelectContextValue {
  value: string;
  onValueChange: (value: string) => void;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  selectedLabel: string;
  setSelectedLabel: (label: string) => void;
  registerLabel: (itemValue: string, label: string) => void;
  triggerRef: React.RefObject<HTMLButtonElement>;
}

const SelectContext = createContext<SelectContextValue | undefined>(undefined);

const useSelectContext = () => {
  const context = useContext(SelectContext);
  if (!context) {
    throw new Error('Select components must be used within a Select provider');
  }
  return context;
};

export const Select = ({
  value: controlledValue,
  onValueChange,
  defaultValue = '',
  children,
  ...props
}: {
  value?: string;
  onValueChange?: (value: string) => void;
  defaultValue?: string;
  children: React.ReactNode;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState('');
  // Ref to the trigger button so the (portaled) menu can anchor to it.
  const triggerRef = useRef<HTMLButtonElement>(null);
  // Support uncontrolled mode with defaultValue
  const [internalValue, setInternalValue] = useState(defaultValue);
  // Counter to trigger re-check when items register
  const [labelVersion, setLabelVersion] = useState(0);

  // Use controlled value if provided, otherwise use internal state
  const value = controlledValue !== undefined ? controlledValue : internalValue;

  // Function to register a label for a value (called by SelectItem)
  const labelMapRef = useRef<Map<string, string>>(new Map());
  // Store current value in ref so registerLabel can access it
  const valueRef = useRef(value);
  valueRef.current = value;

  const registerLabel = useCallback((itemValue: string, label: string) => {
    const prev = labelMapRef.current.get(itemValue);
    labelMapRef.current.set(itemValue, label);
    // If this is a new registration and matches current value, trigger update
    if (prev !== label && itemValue === valueRef.current) {
      setLabelVersion(v => v + 1);
    }
  }, []);

  // When controlled value changes OR when items register, update the label
  useEffect(() => {
    if (value && labelMapRef.current.has(value)) {
      const label = labelMapRef.current.get(value)!;
      if (selectedLabel !== label) {
        setSelectedLabel(label);
      }
    } else if (!value && selectedLabel) {
      setSelectedLabel('');
    }
  }, [value, labelVersion]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleValueChange = (newValue: string) => {
    if (onValueChange) {
      onValueChange(newValue);
    }
    // Always update internal value for uncontrolled mode
    setInternalValue(newValue);
  };

  return (
    <SelectContext.Provider value={{ value, onValueChange: handleValueChange, isOpen, setIsOpen, selectedLabel, setSelectedLabel, registerLabel, triggerRef }}>
      <div className="relative" {...props}>
        {children}
      </div>
    </SelectContext.Provider>
  );
};

export const SelectTrigger = ({
  children,
  className = '',
  ...props
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  const { isOpen, setIsOpen, triggerRef } = useSelectContext();

  return (
    <button
      ref={triggerRef}
      type="button"
      onClick={() => setIsOpen(!isOpen)}
      className={`flex items-center justify-between w-full px-3 py-2 text-sm bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 ${className}`}
      {...props}
    >
      {children}
      <ChevronDown className={`w-4 h-4 ml-2 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
    </button>
  );
};

export const SelectValue = ({
  placeholder = 'Select...',
  ...props
}: {
  placeholder?: string;
}) => {
  const { selectedLabel } = useSelectContext();

  return (
    <span className="text-gray-900 dark:text-gray-100" {...props}>
      {selectedLabel || placeholder}
    </span>
  );
};

export const SelectContent = ({
  children,
  className = '',
  ...props
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  const { isOpen, setIsOpen, triggerRef } = useSelectContext();
  const contentRef = useRef<HTMLDivElement>(null);
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});

  // Anchor the menu to the trigger using viewport (fixed) coordinates. Rendering
  // the menu in a portal on document.body — instead of as an `absolute` child of
  // the trigger — lets it escape any ancestor with `overflow: hidden/auto` (e.g. a
  // modal body with `overflow-y-auto`), which previously clipped the options list
  // (TRUS-351, and the activity entry form in TRUS-350).
  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const GAP = 4;
    const spaceBelow = window.innerHeight - rect.bottom - GAP * 2;
    const spaceAbove = rect.top - GAP * 2;
    // Long catalogs (the IFR document-type list is ~28 entries — TRUS-423) always
    // overflow, so flip the menu above the trigger when there is more room there.
    const dropUp = spaceBelow < 160 && spaceAbove > spaceBelow;
    // Cap the menu height to the room actually available (min 120px) so it never
    // runs off-screen; it scrolls internally beyond that.
    const maxHeight = Math.max(120, Math.min(320, dropUp ? spaceAbove : spaceBelow));
    setMenuStyle({
      position: 'fixed',
      ...(dropUp
        ? { bottom: window.innerHeight - rect.top + GAP }
        : { top: rect.bottom + GAP }),
      left: rect.left,
      width: rect.width,
      maxHeight,
      zIndex: 10000,
      // A modal Radix Dialog sets `pointer-events: none` on <body> while open
      // (disableOutsidePointerEvents). Because this menu is portaled to
      // document.body (to escape ancestor overflow clipping — TRUS-351), it sits
      // OUTSIDE the dialog subtree and inherits that, so its options render but
      // aren't clickable: the click falls through to the dialog beneath, which the
      // outside-click handler reads as a dismiss, so the menu closes without
      // selecting anything. Forcing pointer events back to auto on the menu itself
      // keeps the options selectable inside modals. (TRUS-365)
      pointerEvents: 'auto',
    });
  }, [triggerRef]);

  useEffect(() => {
    if (!isOpen) return;
    updatePosition();

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const inContent = contentRef.current?.contains(target);
      const inTrigger = triggerRef.current?.contains(target);
      if (!inContent && !inTrigger) {
        setIsOpen(false);
      }
    };

    // `true` (capture) keeps the menu anchored while scrolling any ancestor.
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, setIsOpen, updatePosition, triggerRef]);

  // A modal Radix Dialog wraps its content in react-remove-scroll, which installs
  // a NON-PASSIVE `wheel`/`touchmove` listener on `document` and preventDefault()s
  // every such event that originates outside the dialog subtree. This menu is
  // portaled to document.body (TRUS-351), so it counts as "outside": the option
  // list rendered with its own scrollbar but the wheel/trackpad did nothing, which
  // is invisible on platforms with overlay scrollbars — the list simply looked
  // unscrollable (TRUS-423). Stopping the event on the menu keeps it from ever
  // reaching that document-level listener, so the browser scrolls the menu
  // natively. Registered non-passively for parity with the listener we bypass.
  useEffect(() => {
    const node = contentRef.current;
    if (!isOpen || !node) return;
    const stopScrollPropagation = (event: Event) => event.stopPropagation();
    node.addEventListener('wheel', stopScrollPropagation, { passive: false });
    node.addEventListener('touchmove', stopScrollPropagation, { passive: false });
    return () => {
      node.removeEventListener('wheel', stopScrollPropagation);
      node.removeEventListener('touchmove', stopScrollPropagation);
    };
  }, [isOpen]);

  // Keep the items mounted while closed (hidden, no layout) so each SelectItem can
  // register its label — the trigger shows the selected value before the menu is
  // ever opened.
  if (!isOpen || typeof document === 'undefined') {
    return <div className="hidden">{children}</div>;
  }

  return createPortal(
    <div
      ref={contentRef}
      // Stop pointer/mouse-down from bubbling to document so a Radix Dialog's
      // dismissable layer doesn't treat a click on this (body-portaled) menu as an
      // outside click and close the whole modal.
      onPointerDown={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      className={`bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg dark:shadow-gray-900/50 overflow-auto overscroll-contain ${className || ''}`}
      style={menuStyle}
      {...props}
    >
      {children}
    </div>,
    document.body,
  );
};

/**
 * Extract text content from React nodes for display purposes
 */
const extractTextFromChildren = (children: React.ReactNode): string => {
  if (typeof children === 'string') return children;
  if (typeof children === 'number') return String(children);
  if (!children) return '';

  // Handle arrays of children
  if (Array.isArray(children)) {
    return children.map(extractTextFromChildren).filter(Boolean).join(' ');
  }

  // Handle React elements - extract text from their children
  if (React.isValidElement(children)) {
    const props = children.props as { children?: React.ReactNode };
    if (props.children) {
      return extractTextFromChildren(props.children);
    }
  }

  return '';
};

export const SelectItem = ({
  value,
  children,
  textValue,
  className = '',
  ...props
}: {
  value: string;
  children: React.ReactNode;
  textValue?: string;  // Optional text to display in trigger when selected
  className?: string;
}) => {
  const { value: selectedValue, onValueChange, setIsOpen, setSelectedLabel, registerLabel } = useSelectContext();
  const isSelected = selectedValue === value;

  // Determine the label to use: textValue prop, or extracted text from children
  const displayLabel = textValue ?? extractTextFromChildren(children);

  // Register this item's label with the parent Select on mount
  // This allows the Select to display the correct label even before dropdown opens
  useEffect(() => {
    registerLabel(value, displayLabel);
  }, [value, displayLabel, registerLabel]);

  return (
    <div
      onClick={() => {
        onValueChange(value);
        setSelectedLabel(displayLabel);
        setIsOpen(false);
      }}
      className={`px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 ${isSelected ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-900 dark:text-blue-100 font-medium' : 'text-gray-900 dark:text-gray-100'} ${className}`}
      {...props}
    >
      {children}
    </div>
  );
};
